import * as React from "react";
import { useContext, useState } from "react";
import {
  AddMembersContext,
  ApiErrorHandler,
  ChatInfoContext,
  OrganizationContext,
  useFetchChatInfo,
  useTranslation,
} from "hhn-commons";
import { RightDrawerContext } from "../context/RightDrawerContext";
import AddMember from "./AddMember";
import { useSnackbar } from "notistack";
import {
  FEATURES,
  GROUP_TYPE,
  IBroadcastInfo,
  IGroupInfo,
  ISelectedMembers,
  USER_TYPE,
} from "@home-health-notify/domain";
import CommonDrawerHeader from "../common/CommonDrawerHeader";
import { useOutsideClick } from "../utils/UseOutsideClick";
import { AddGroupWrapper } from "../common/Common.style";
import {
  getAdvanceSearchFilters,
  getPatientHeaderProps,
} from "./addUsers/advanceSearch/map/AdvanceMapHelper";

const AddMemberContainer = ({
  showBackButton,
  chatId,
  groupType,
}: {
  showBackButton: boolean;
  chatId: string;
  groupType: GROUP_TYPE;
}) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { openChatInfoDrawer, closeDrawer } = useContext(RightDrawerContext);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const tNsDrawer = (key: string) => t(key, { ns: "drawerHeader" });
  const tNsMessage = (key: string) => t(key, { ns: "message" });
  const { setChatInfoContext } = useFetchChatInfo();

  React.useEffect(() => {
    (async () => {
      await setChatInfoContext(chatId, groupType);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { chat } = useContext(ChatInfoContext);
  const { resetMembers } = useContext(AddMembersContext);

  const groupInfo = chat?.chatInfo as IGroupInfo | IBroadcastInfo;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isBroadcastGroup = groupInfo?.groupType === GROUP_TYPE.BROADCAST;
  const isPatientGroup = groupInfo?.groupType === GROUP_TYPE.PATIENT;
  const isSendReferralsAllowed = selectedOrgUser?.hasAccess(
    FEATURES.SEND_REFERRALS,
  );
  const enableReferrals = isPatientGroup && isSendReferralsAllowed;
  const isInternalGroup = groupInfo?.groupType === GROUP_TYPE.NON_PATIENT;
  const userFilters = {
    type: (type: USER_TYPE) => type !== USER_TYPE.PATIENT,
  };

  const addMembers = async (selectedMembers: ISelectedMembers) => {
    const chatId: string = groupInfo.id;
    try {
      setIsLoading(true);

      if (isBroadcastGroup) {
        await selectedOrgUser?.chatManager
          .getBroadcastChatManager()
          .addMembers(selectedMembers, chatId);
      } else {
        await selectedOrgUser?.chatManager
          .getPatientChatManager()
          .addMembers(selectedMembers, chatId);
      }

      setIsLoading(false);

      enqueueSnackbar(
        groupInfo?.name + tNsMessage("membersAddedSuccessfully"),
        {
          autoHideDuration: 3000,
        },
      );
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      resetMembers();
      closeDrawer();
    }
  };

  const openChatInfo = () => {
    openChatInfoDrawer(1);
  };

  const close = () => {
    closeDrawer();
    resetMembers();
  };

  return (
    <>
      <AddGroupWrapper
        ref={useOutsideClick()}
        data-testid="add-member-container-component"
      >
        {groupInfo && (
          <>
            <CommonDrawerHeader
              showBackButton={showBackButton}
              onBackButtonClick={openChatInfo}
              title={tNsDrawer("addMembers")}
              onCloseButtonClick={close}
            />
            <AddMember
              existingGroup={true}
              callback={addMembers}
              existingMember={groupInfo.members}
              isLoading={isLoading}
              hideGroupsTab={isBroadcastGroup}
              enableReferrals={enableReferrals}
              userDirectoryFilters={isInternalGroup ? userFilters : {}}
              advanceSearchFilters={getAdvanceSearchFilters(groupInfo)}
              patientHeaderProps={getPatientHeaderProps(groupInfo)}
            />
          </>
        )}
      </AddGroupWrapper>
    </>
  );
};

export default AddMemberContainer;
